import { useState, useEffect } from 'react'
import WebApp from '@twa-dev/sdk'
import { ChevronLeftIcon, ShoppingBagIcon, UserIcon, ClockIcon } from '../Icons'

interface AdminPanelProps {
  onClose: () => void
  onLog: (message: string) => void
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  red: '#ef4444',
}

interface Order {
  id: string
  user: string
  items: number
  total: string
  status: 'pending' | 'processing' | 'completed'
  date: string
}

export default function AdminPanel({ onClose, onLog }: AdminPanelProps) {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Загружаем заказы из CloudStorage
    loadOrders()
  }, [])

  const loadOrders = async () => {
    try {
      const keys = (await WebApp.CloudStorage.getKeys()) as any as string[]
      const orderKeys = keys.filter((key: string) => key.startsWith('order_'))
      
      const ordersData: Order[] = []
      for (const key of orderKeys) {
        const data = (await WebApp.CloudStorage.getItem(key)) as any as string
        if (data && data.length > 0) {
          try {
            const orderData = JSON.parse(data)
            ordersData.push(orderData)
          } catch (e) {
            console.error('Error parsing order:', e)
          }
        }
      }
      
      setOrders(ordersData.sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      ))
      onLog(`📊 Загружено заказов: ${ordersData.length}`)
    } catch (error) {
      onLog('❌ Ошибка загрузки заказов')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.green
      case 'processing': return colors.yellow
      default: return colors.accent
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Выполнен'
      case 'processing': return 'В работе'
      default: return 'Новый'
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text,
      paddingBottom: '24px',
    }}>
      {/* Шапка */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
        padding: '16px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <ChevronLeftIcon size={20} color="#fff" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              margin: 0, 
              fontSize: '20px', 
              fontWeight: 700,
              color: '#fff',
            }}>
              👨‍💼 Панель администратора
            </h1>
            <p style={{ 
              margin: '4px 0 0 0',
              fontSize: '13px',
              opacity: 0.9,
              color: '#fff',
            }}>
              Управление заказами
            </p>
          </div>
        </div>
      </div>

      {/* Статистика */}
      <div style={{
        padding: '16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
      }}>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: colors.accent }}>
            {orders.length}
          </div>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
            Всего
          </div>
        </div>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: colors.yellow }}>
            {orders.filter(o => o.status === 'processing').length}
          </div>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
            В работе
          </div>
        </div>
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: colors.green }}>
            {orders.filter(o => o.status === 'completed').length}
          </div>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
            Завершено
          </div>
        </div>
      </div>

      {/* Список заказов */}
      <div style={{ padding: '0 16px' }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: 600,
          margin: '24px 0 12px 0',
          color: colors.textMuted,
        }}>
          Последние заказы
        </h2>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: colors.textMuted,
          }}>
            Загрузка...
          </div>
        ) : orders.length === 0 ? (
          <div style={{
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
          }}>
            <ShoppingBagIcon size={48} color={colors.textMuted} />
            <p style={{
              margin: '16px 0 0 0',
              color: colors.textMuted,
            }}>
              Заказов пока нет
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {orders.map((order) => (
              <div
                key={order.id}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.cardBorder}`,
                  borderRadius: '12px',
                  padding: '16px',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                }}>
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px',
                    }}>
                      <UserIcon size={16} color={colors.textMuted} />
                      <span style={{ fontWeight: 600 }}>{order.user}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      color: colors.textMuted,
                    }}>
                      <ClockIcon size={14} color={colors.textMuted} />
                      {order.date}
                    </div>
                  </div>
                  <div
                    style={{
                      background: getStatusColor(order.status) + '20',
                      color: getStatusColor(order.status),
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {getStatusLabel(order.status)}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '12px',
                  borderTop: `1px solid ${colors.cardBorder}`,
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: colors.textMuted,
                  }}>
                    <ShoppingBagIcon size={16} color={colors.textMuted} />
                    {order.items} {order.items === 1 ? 'товар' : 'товара'}
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: colors.accent,
                  }}>
                    {order.total}
                  </div>
                </div>

                {/* Кнопки действий админа */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '12px',
                }}>
                  <button
                    onClick={() => onLog(`✅ Заказ ${order.id} подтверждён`)}
                    style={{
                      flex: 1,
                      background: `${colors.green}20`,
                      border: `1px solid ${colors.green}50`,
                      borderRadius: '8px',
                      padding: '10px',
                      color: colors.green,
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ✓ Подтвердить
                  </button>
                  <button
                    onClick={() => onLog(`❌ Заказ ${order.id} отменён`)}
                    style={{
                      flex: 1,
                      background: `${colors.red}20`,
                      border: `1px solid ${colors.red}50`,
                      borderRadius: '8px',
                      padding: '10px',
                      color: colors.red,
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ✕ Отменить
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
